import {
  CreateNewPrefLabelSchema,
  PrefSlotSchema,
} from '@youshift/shared/hooks/mutations';
import {
  PreferenceLabel,
  PreferenceSlot,
  SectionSlot,
  SlotLabel,
} from '@youshift/shared/types';
import {
  ColorName,
  colorNames,
  dateToString,
  getRandomColor,
  getRandomIcon,
  LabelIcon,
  LabelIcons,
} from '@youshift/shared/utils';
import { v4 as uuidv4 } from 'uuid';

export type PrefSlotsMapAction =
  | { type: 'ORPHAN_SECTION_SLOT'; sectionSlotId: number }
  | {
      type: 'ASSIGN_TO_PREF_SLOT';
      sectionSlotId: number;
      prefSlotId: number;
    };

export const generateDefaultPrefConfiguration = (
  sectionSlots: Record<number, SectionSlot>,
  slotLabels: Record<number, SlotLabel>,
): CreateNewPrefLabelSchema[] => {
  // Step 1: Get all unique sets of slot_labels for all section_slots
  const uniqueSlotLabelSets = new Map<
    string,
    {
      name: string;
      icon: LabelIcon;
      color: ColorName;
    }
  >();

  // Collect all unique slot label sets
  Object.values(sectionSlots).forEach(slot => {
    if (!slot.slot_labels || slot.slot_labels.length === 0) return;

    // Sort to ensure consistent key generation
    const sortedLabelIds = [...slot.slot_labels].sort((a, b) => a - b);
    const key = sortedLabelIds.join(',');

    if (!uniqueSlotLabelSets.has(key)) {
      // Get all labels in the set
      const labels = sortedLabelIds.map(id => slotLabels[id]);

      // Concatenate names with '+' or '-'
      const combinedName = labels.map(label => label.name).join(' + ');

      uniqueSlotLabelSets.set(key, {
        name: combinedName,
        icon: getRandomIcon(),
        color: getRandomColor(),
      });
    }
  });

  // Step 2: For each unique set, create a pref_label with pref_slots
  const result: CreateNewPrefLabelSchema[] = [];

  uniqueSlotLabelSets.forEach((labelSet, key) => {
    // Group slots by start day
    const slotsByStartDay = new Map<string, number[]>();

    Object.entries(sectionSlots).forEach(([slotId, slot]) => {
      if (!slot.slot_labels) return;

      const sortedSlotLabels = [...slot.slot_labels].sort((a, b) => a - b);
      const slotLabelKey = sortedSlotLabels.join(',');

      if (slotLabelKey === key) {
        const startDay = dateToString(slot.start, 'iso');
        if (!slotsByStartDay.has(startDay)) {
          slotsByStartDay.set(startDay, []);
        }
        slotsByStartDay.get(startDay)!.push(Number(slotId));
      }
    });

    // Create pref_label with pref_slots grouped by start day
    result.push({
      pref_label: {
        name: labelSet.name,
        icon: labelSet.icon,
        color: labelSet.color,
      },
      pref_slots: Array.from(slotsByStartDay.values()).map(sectionSlotIds => ({
        section_slots: sectionSlotIds,
        display_date: dateToString(
          new Date(
            Math.min(
              ...sectionSlotIds.map(id =>
                new Date(sectionSlots[id].start).getTime(),
              ),
            ),
          ),
          'iso',
        ),
      })),
    });
  });

  return result;
};

// type PrefSlotsConfigWithUUIDs = {
//   preferenceSlots: Record<number, PreferenceSlot>;
//   prefLabels: Record<number, PreferenceLabel>;
// };

// export const simulateInitialStateWithUUIDs = (
//   initialState: CreateNewPrefLabelSchema[],
//   id_itr: number,
// ): PrefSlotsConfigWithUUIDs => {
//   const preferenceSlots: Record<number, PreferenceSlot> = {};
//   const prefLabels: Record<number, PreferenceLabel> = {};

//   initialState.forEach((schema, index) => {
//     // Create a unique ID for the preference label
//     const prefLabelId = index + 1; // In real backend this would be auto-incremented

//     // Create the preference label
//     prefLabels[prefLabelId] = {
//       id_pref_label: prefLabelId,
//       id_itr,
//       name: schema.pref_label.name,
//       created: new Date().toISOString(),
//       icon: schema.pref_label.icon,
//       color: schema.pref_label.color,
//       base_id_pref_label: null,
//     };

//     // Create preference slots for this label
//     schema.pref_slots.forEach((prefSlot, slotIndex) => {
//       const prefSlotId = uuidv4() as unknown as number;

//       preferenceSlots[prefSlotId] = {
//         id_pref_slot: prefSlotId,
//         id_itr,
//         id_pref_label: prefLabelId,
//         section_slots: prefSlot.section_slots,
//       };
//     });
//   });

//   return {
//     preferenceSlots,
//     prefLabels,
//   };
// };

// export const getNewPrefLabelSchemaFromUUIDs = (
//   preferenceSlots: Record<number, PreferenceSlot>,
//   prefLabels: Record<number, PreferenceLabel>,
// ): CreateNewPrefLabelSchema[] => {
//   const result: CreateNewPrefLabelSchema[] = [];

//   // Group preference slots by their label ID
//   const slotsByLabelId: Record<number, PreferenceSlot[]> = {};

//   Object.values(preferenceSlots).forEach(slot => {
//     if (!slotsByLabelId[slot.id_pref_label]) {
//       slotsByLabelId[slot.id_pref_label] = [];
//     }
//     slotsByLabelId[slot.id_pref_label].push(slot);
//   });

//   // For each preference label, create a schema entry
//   Object.values(prefLabels).forEach(label => {
//     const labelSlots = slotsByLabelId[label.id_pref_label] || [];

//     result.push({
//       pref_label: {
//         name: label.name,
//         icon: label.icon as LabelIcon,
//         color: label.color as ColorName,
//       },
//       pref_slots: labelSlots.map(slot => ({
//         section_slots: slot.section_slots,
//       })),
//     });
//   });

//   return result;
// };

// /**
//  * Helper function that generates default preference configuration and simulates
//  * initial state with UUIDs in one step
//  * @param sectionSlots Record of section slots
//  * @param slotLabels Record of slot labels
//  * @param id_itr Iteration ID
//  * @returns Complete preference configuration with UUIDs
//  */
// export const generateDefaultPrefConfigWithUUIDs = (
//   sectionSlots: Record<number, SectionSlot>,
//   slotLabels: Record<number, SlotLabel>,
//   id_itr: number,
// ): PrefSlotsConfigWithUUIDs => {
//   // First generate the default preference configuration
//   const defaultConfig = generateDefaultPrefConfiguration(
//     sectionSlots,
//     slotLabels,
//   );

//   // Then simulate the initial state with UUIDs
//   return simulateInitialStateWithUUIDs(defaultConfig, id_itr);
// };

export const getNewPrefSlotsFromSectionSlots = (
  sectionSlots: Record<number, SectionSlot>,
  id_pref_label: number,
  id_itr: number,
): Record<number, PreferenceSlot> => {
  // Group slots by start day
  const slotsByStartDay = new Map<string, number[]>();

  Object.entries(sectionSlots).forEach(([slotId, slot]) => {
    const startDay = dateToString(slot.start, 'iso');
    if (!slotsByStartDay.has(startDay)) {
      slotsByStartDay.set(startDay, []);
    }
    slotsByStartDay.get(startDay)!.push(Number(slotId));
  });

  // Create one temporary pref slot per day, using UUIDs
  return Object.fromEntries(
    Array.from(slotsByStartDay.entries()).map(([startDay, sectionSlotIds]) => {
      const uuid = uuidv4() as unknown as number;
      return [
        uuid,
        {
          id_pref_slot: uuid,
          section_slots: sectionSlotIds,
          id_pref_label,
          id_itr,
          display_date: startDay,
        },
      ];
    }),
  );
};
